import { CountrySchema, useCountries, useIntl, type UserAMLQuestionnaireRequirementConfig } from '@capsicum/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { amlQuestionnaireReducerAtom } from '../../data-access';

type PostalAddressFormType = z.infer<ReturnType<typeof usePostalAddressFormSchema>>;

const usePostalAddressFormSchema = () => {
	const { $t } = useIntl();

	return z.object({
		addressBuildingNumber: z
			.string()
			.nonempty(
				$t('requirements.common.form.errors.required', {
					field: $t('global.addressBuildingNumber')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.addressBuildingNumber'),
					max: 255,
					min: 1,
					value: $t('requirements.common.form.errors.long')
				})
			),
		addressMunicipality: z.union([
			z
				.string()
				.min(
					2,
					$t('requirements.common.form.errors.length', {
						field: $t('global.addressMunicipality'),
						max: 255,
						min: 2,
						value: $t('requirements.common.form.errors.short')
					})
				)
				.max(
					255,
					$t('requirements.common.form.errors.length', {
						field: $t('global.addressMunicipality'),
						max: 255,
						min: 2,
						value: $t('requirements.common.form.errors.long')
					})
				),
			z.undefined(),
			z.string().length(0)
		]),
		city: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('global.addressCity'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.addressCity'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		countryCode: CountrySchema,
		region: z.union([
			z
				.string()
				.min(
					2,
					$t('requirements.common.form.errors.length', {
						field: $t('global.addressProvince'),
						max: 255,
						min: 2,
						value: $t('requirements.common.form.errors.short')
					})
				)
				.max(
					255,
					$t('requirements.common.form.errors.length', {
						field: $t('global.addressProvince'),
						max: 255,
						min: 2,
						value: $t('requirements.common.form.errors.long')
					})
				),
			z.undefined(),
			z.string().length(0)
		]),
		street: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('global.addressStreet'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.addressStreet'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		zipCode: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('global.addressPostcode'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.addressPostcode'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			)
	});
};

export const useAmlPostalAddress = (prefilledData: UserAMLQuestionnaireRequirementConfig['prefill']) => {
	const { countriesQuery, getCountryBy } = useCountries();
	const dispatch = useSetAtom(amlQuestionnaireReducerAtom);

	const defaultValues = {
		addressBuildingNumber: '',
		addressMunicipality: '',
		city: prefilledData.userAddress.city || '',
		countryCode: getCountryBy(prefilledData.userAddress.countryCode || undefined, 'iso2'),
		region: prefilledData.userAddress.region || '',
		street: prefilledData.userAddress.street || '',
		zipCode: prefilledData.userAddress.zipCode || ''
	};

	const postalAddressForm = useForm<PostalAddressFormType>({
		defaultValues,
		delayError: 200,
		mode: 'onChange',
		resolver: zodResolver(usePostalAddressFormSchema())
	});

	useEffect(() => {
		if (countriesQuery.isFetched) {
			postalAddressForm.reset(defaultValues);
		}
	}, [countriesQuery.isFetched, countriesQuery.isFetching]);

	const formValues = postalAddressForm.watch();
	const isValid = postalAddressForm.formState.isValid;

	const previousValuesRef = useRef(formValues);
	const previousValidityRef = useRef(isValid);

	useEffect(() => {
		const hasFormValuesChanged = JSON.stringify(previousValuesRef.current) !== JSON.stringify(formValues);
		const hasValidityChanged = previousValidityRef.current !== isValid;

		if (hasFormValuesChanged || hasValidityChanged) {
			previousValuesRef.current = formValues;
			previousValidityRef.current = isValid;

			dispatch({ isValid, stepId: 'PostalAddress', type: 'UPDATE_STEP_VALIDITY' });
			dispatch({
				data: { ...formValues, countryCode: formValues.countryCode?.iso2 },
				type: 'UPDATE_FORM_DATA'
			});
		}
	}, [formValues, isValid]);

	return {
		postalAddressForm
	};
};
